import { useState, type FormEvent } from 'react';
import { useMutation } from '@tanstack/react-query';
import { PackagePlus, QrCode } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card, CardBody, CardHeader } from '@/components/ui/Card';
import { Button, ButtonLink } from '@/components/ui/Button';
import { TextField } from '@/components/ui/Field';
import { ShowOnceSecret, type SecretRow } from '@/components/domain/ShowOnceSecret';
import { provisionDevices } from '@/api/endpoints/devices';
import { errorMessage } from '@/api/errors';
import { downloadClaimLabels } from '@/lib/claimLabels';
import type { DeviceProvisioned } from '@/types/api';

/**
 * Manufacturing-side device creation. This is the only endpoint in the system that
 * mints a device identity, and the only response that ever contains a plaintext
 * secret — consumer endpoints deliberately cannot do this, because a device
 * credential can forge telemetry and forged telemetry dispatches an ambulance.
 */
export function ProvisioningPage() {
  const [count, setCount] = useState('1');
  const [hwBatch, setHwBatch] = useState('');
  const [batch, setBatch] = useState<DeviceProvisioned[] | null>(null);
  const [labelsBusy, setLabelsBusy] = useState(false);

  const mutation = useMutation({
    mutationFn: () => provisionDevices({ count: Number(count), hw_batch: hwBatch.trim() }),
    onSuccess: (devices) => setBatch(devices),
  });

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    mutation.mutate();
  }

  if (batch) {
    const rows: SecretRow[] = batch.flatMap((device, index) => {
      const entries: SecretRow[] = [
        { label: `#${index + 1} Device ID`, value: device.device_uid },
        { label: `#${index + 1} Claim code`, value: device.claim_code, isClaimCode: true },
      ];
      // Secure-element units hold their key in hardware and return no secret.
      if (device.secret) {
        entries.push({ label: `#${index + 1} Secret`, value: device.secret });
      }
      return entries;
    });

    return (
      <>
        <PageHeader
          title={`${batch.length} device${batch.length === 1 ? '' : 's'} provisioned`}
          description="Record these before leaving this page."
          action={
            <span className="flex gap-2 print-hidden">
              <Button
                loading={labelsBusy}
                onClick={async () => {
                  setLabelsBusy(true);
                  try {
                    await downloadClaimLabels(batch, `autoaware-labels-${hwBatch.trim() || Date.now()}`);
                  } finally {
                    setLabelsBusy(false);
                  }
                }}
              >
                <QrCode className="size-4" aria-hidden />
                Download labels (.xlsx)
              </Button>
              <Button
                onClick={() => {
                  setBatch(null);
                  mutation.reset();
                }}
              >
                Provision more
              </Button>
              <ButtonLink to="/admin/devices" variant="primary">
                View registry
              </ButtonLink>
            </span>
          }
        />

        <Card>
          <CardBody>
            <ShowOnceSecret rows={rows} filename={`autoaware-batch-${Date.now()}`} />
          </CardBody>
        </Card>
      </>
    );
  }

  return (
    <>
      <PageHeader
        title="Provision devices"
        description="Create device identities for manufacturing. Each gets a claim code printed on its card."
      />

      <Card className="max-w-lg">
        <CardHeader title="New batch" />
        <CardBody>
          <form onSubmit={handleSubmit} className="space-y-4">
            <TextField
              label="How many"
              type="number"
              min={1}
              max={500}
              value={count}
              onChange={(event) => setCount(event.target.value)}
              hint="Between 1 and 500."
              required
            />
            <TextField
              label="Hardware batch"
              value={hwBatch}
              onChange={(event) => setHwBatch(event.target.value)}
              placeholder="e.g. B-2026-08"
              hint="The manufacturing run these units belong to. Recorded against every device so a bad batch can be traced."
              required
            />

            {mutation.error && (
              <p className="rounded-[--radius-control] bg-bad-soft px-3 py-2 text-[13px] text-bad" role="alert">
                {errorMessage(mutation.error)}
              </p>
            )}

            <div className="rounded-[--radius-control] bg-surface-sunken px-3 py-2.5 text-[13px] text-ink-secondary">
              Claim codes and secrets are returned once and are not stored in retrievable form.
              Nothing recovers them afterwards.
            </div>

            <Button
              type="submit"
              variant="primary"
              loading={mutation.isPending}
              disabled={hwBatch.trim().length === 0}
            >
              <PackagePlus className="size-4" aria-hidden />
              Provision
            </Button>
          </form>
        </CardBody>
      </Card>
    </>
  );
}
